import Crud from "../Crud";
import MUIBrownColor from "@mui/material/colors/brown";

/**
 * Static helper functions related to Products.
 */
export default class Product extends Crud {
  /**
   * @param {object} product The product object.
   * @returns The display name of the product.
   */
  static getDisplayName(product) {
    return product ? product.id : null;
  }

  /**
   * @returns {string} The color of this object.
   */
  static getBaseColor() {
    return MUIBrownColor;
  }

  /**
   * @returns {string} The icon name for this object.
   */
  static getIconName() {
    return "Inventory";
  }

  /**
   * Returns the points value for a given product and insuranceTypeId. If
   * product.billable !== true, returns 0. Searches
   * productMatrixEntriesIndexed for a matching insuranceTypeId and productId.
   * If no match is found, searches insuranceMatrixEntriesIndexed for a
   * matching insuranceTypeId and productTypeId. Returns 0 if no match is
   * found.
   *
   * @param {object} productSubType The product subtype object.
   * @param {number} insuranceTypeId The insurance type ID.
   * @param {object} insuranceMatrixEntriesIndexed Object of insurance matrix
   * entries, indexed by ID.
   * @param {object} productMatrixEntriesIndexed Object of product matrix
   * entries, indexed by ID.
   * @param {object} [product] The product object (optional).
   * @returns {number} The points value, or 0 if no match is found or billable
   * is not true.
   */
  static getPoints(
    productSubType,
    insuranceTypeId,
    insuranceMatrixEntriesIndexed,
    productMatrixEntriesIndexed,
    product,
  ) {
    if (!productSubType) {
      // Handles when these have been deleted and null is passed to the function.
      return 0;
    }

    if (product && product.billable !== true) {
      return 0;
    }

    for (const productMatrixEntry of Object.values(
      productMatrixEntriesIndexed || {},
    )) {
      if (
        productMatrixEntry.insuranceTypeId === insuranceTypeId &&
        productMatrixEntry.productSubTypeId === productSubType.id
      ) {
        return productMatrixEntry.points || 0;
      }
    }

    for (const insuranceMatrixEntry of Object.values(
      insuranceMatrixEntriesIndexed || {},
    )) {
      if (
        insuranceMatrixEntry.insuranceTypeId === insuranceTypeId &&
        insuranceMatrixEntry.productTypeId === productSubType.productTypeId
      ) {
        return insuranceMatrixEntry.points || 0;
      }
    }

    return 0;
  }

  /**
   * @param {string} hcpcs The hcpcs code.
   * @param {boolean} isRental Whether the product is a rental.
   * @param {object} hcpcsCodesIndexed Object of hcpcs codes, indexed by ID.
   * @param {object} feeSchedulesIndexed Object of fee schedules, indexed by ID.
   * @returns {number} The product amount.
   */
  static getAmount(hcpcs, isRental, hcpcsCodesIndexed, feeSchedulesIndexed) {
    // For now we assume the purchase price for everything.
    // const modifierCodeId = isRental === true ? 2 : 1;
    const modifierCodeId = 1;

    const feeSchedule = this.getProductFeeScheduleMatch(
      hcpcs,
      modifierCodeId,
      hcpcsCodesIndexed,
      feeSchedulesIndexed,
    );

    if (!feeSchedule) {
      return null;
    }

    return this.getProductLineAmount(
      hcpcs,
      modifierCodeId,
      hcpcsCodesIndexed,
      feeSchedulesIndexed,
    );
  }

  /**
   * @param {string} hcpcs The hcpcs code.
   * @param {number} modifierCodeId The modifier code id.
   * @param {object} hcpcsCodesIndexed Object of hcpcs codes, indexed by ID.
   * @param {object} feeSchedulesIndexed Object of fee schedules, indexed by ID.
   * @returns {object} The product fee schedule match.
   */
  static getProductFeeScheduleMatch(
    hcpcs,
    modifierCodeId,
    hcpcsCodesIndexed,
    feeSchedulesIndexed,
  ) {
    const normalizedHcpcs = String(hcpcs || "")
      .toUpperCase()
      .substring(0, 5);
    if (!normalizedHcpcs) {
      return null;
    }

    const hcpcsMatch = Object.values(hcpcsCodesIndexed || {}).find(
      (hcpcsCode) => hcpcsCode.code === normalizedHcpcs,
    );
    if (!hcpcsMatch) {
      return null;
    }

    return (
      Object.values(feeSchedulesIndexed || {}).find(
        (item) =>
          item.hcpcsCodeId === hcpcsMatch.id &&
          item.modifierCodeId === modifierCodeId,
      ) || null
    );
  }

  /**
   * @param {string} hcpcs The hcpcs code.
   * @param {number} modifierCodeId The modifier code id.
   * @param {object} hcpcsCodesIndexed Object of hcpcs codes, indexed by ID.
   * @param {object} feeSchedulesIndexed Object of fee schedules, indexed by ID.
   * @returns {number} The product line amount.
   */
  static getProductLineAmount(
    hcpcs,
    modifierCodeId,
    hcpcsCodesIndexed,
    feeSchedulesIndexed,
  ) {
    const normalizedHcpcs = (hcpcs || "").toUpperCase().substring(0, 5);
    if (!normalizedHcpcs) {
      return 0;
    }

    const hcpcsMatch = Object.values(hcpcsCodesIndexed || {}).find(
      (hcpcsCode) => hcpcsCode.code === normalizedHcpcs,
    );
    if (!hcpcsMatch) {
      return 0;
    }

    const feeSchedule = Object.values(feeSchedulesIndexed || {}).find(
      (item) =>
        item.hcpcsCodeId === hcpcsMatch.id &&
        item.modifierCodeId === modifierCodeId,
    );
    if (!feeSchedule) {
      return 0;
    }

    return Number(feeSchedule.amount.toFixed(2));
  }
}
